import { ROUTE_PATHS } from '@constants/routes';
import EventSeatRoundedIcon from '@mui/icons-material/EventSeatRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton, { type ListItemButtonProps } from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useGetInstallationQuery } from '@store/api/installationEndpoints';
import { Link, useLocation, type LinkProps } from 'react-router-dom';

const ListItemButtonLink = styled(ListItemButton)<ListItemButtonProps & LinkProps>(() => ({
  padding: '2px !important',
  height: '38px',
}));

const StyledStack = styled(Stack)({
  flexGrow: 1,
  padding: '8px',
  justifyContent: 'space-between',
});

const StyledList = styled(List)({
  gap: '0.375rem',
  padding: 0,
});

const listItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  backgroundColor: '#26272F',
  borderRadius: '.375rem',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid rgb(54, 56, 67)',
  '& svg': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const secondaryListItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& svg': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const MenuContent: React.FC<{ toggleDrawer?: () => void }> = ({ toggleDrawer }) => {
  const { data: installation } = useGetInstallationQuery();
  const location = useLocation();
  const currentPath = location.pathname;

  const hasLicenses = (installation?.licenseCount ?? 0) > 0;

  // Licence files are imported on the Licenses page, so it has to be reachable before the first
  // one exists or there is nowhere to do it. It opens once the server is enrolled, which is the
  // point at which a licence file can arrive and do anything. Seat sessions still needs licences:
  // without them the page has nothing to list.
  const isEnrolled = !!installation?.certificate?.installed;

  const mainListItems = [
    { text: 'Enrolment', icon: <VerifiedUserRoundedIcon />, url: ROUTE_PATHS.ACTIVATION, disabled: false },
    { text: 'Licenses', icon: <VpnKeyRoundedIcon />, url: ROUTE_PATHS.LICENSES, disabled: !isEnrolled && !hasLicenses },
    { text: 'Seat sessions', icon: <EventSeatRoundedIcon />, url: ROUTE_PATHS.SEAT_SESSIONS, disabled: !hasLicenses },
    { text: 'Activity', icon: <QueryStatsRoundedIcon />, url: ROUTE_PATHS.ACTIVITY, disabled: !hasLicenses },
  ];

  const secondaryListItems = [
    { text: 'Settings', icon: <SettingsRoundedIcon />, url: ROUTE_PATHS.SETTINGS, disabled: false },
  ];

  const renderItem = (
    item: { text: string, icon: React.ReactNode, url: string, disabled: boolean },
    iconStyles: object
  ) => {
    const button = (
      <ListItemButtonLink
        component={Link}
        to={item.url}
        selected={currentPath.startsWith(item.url)}
        disabled={item.disabled}
        onClick={toggleDrawer}
      >
        <ListItemIcon sx={iconStyles}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButtonLink>
    );

    return (
      <ListItem key={item.url} disablePadding>
        {item.disabled
          ? <Tooltip title='Import a license file first' placement='right'><span style={{ width: '100%' }}>{button}</span></Tooltip>
          : button}
      </ListItem>
    );
  };

  return (
    <StyledStack>
      <StyledList dense>
        {mainListItems.map(item => renderItem(item, listItemIconStyles))}
      </StyledList>
      <StyledList dense sx={{ gap: '.1rem' }}>
        {secondaryListItems.map(item => renderItem(item, secondaryListItemIconStyles))}
      </StyledList>
    </StyledStack>
  );
};

export default MenuContent;
